import React, { useEffect, useState } from 'react';
import { cn } from '../../lib/utils';

// Tetris colors for the grid (Tetris Guideline standard)
const tetrisColors = [
  '#00FFFF', // I piece - Cyan
  '#FFFF00', // O piece - Yellow
  '#800080', // T piece - Purple
  '#00FF00', // S piece - Green
  '#FF0000', // Z piece - Red
  '#0000FF', // J piece - Blue
  '#FFA500', // L piece - Orange
];

interface HeroBackgroundProps {
  children: React.ReactNode;
  className?: string;
}

const HeroBackground: React.FC<HeroBackgroundProps> = ({ children, className }) => {
  const [dimensions, setDimensions] = useState({
    numCols: 0,
    numRows: 0,
    cellSize: 96 // Default to 6rem (96px)
  });

  useEffect(() => {
    const updateDimensions = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      // Adjust cell size based on screen width
      let cellSize = 96; // 6rem = 96px
      if (width < 640) { // sm breakpoint
        cellSize = 48; // 3rem = 48px
      } else if (width < 1024) { // lg breakpoint
        cellSize = 64; // 4rem = 64px
      }
      
      setDimensions({
        numCols: Math.ceil(width / cellSize),
        numRows: Math.ceil(height / cellSize),
        cellSize
      });
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Generate gracefully distributed colored squares
  const totalCells = dimensions.numCols * dimensions.numRows;
  const maxColored = Math.min(30, Math.floor(totalCells * 0.12)); // 12% of grid, max 30

  // Define a central safe area (center 3x3 or 4x4 cells depending on grid size)
  const safeAreaRows = Math.max(3, Math.floor(dimensions.numRows / 4));
  const safeAreaCols = Math.max(3, Math.floor(dimensions.numCols / 4));
  const safeRowStart = Math.floor((dimensions.numRows - safeAreaRows) / 2);
  const safeRowEnd = safeRowStart + safeAreaRows - 1;
  const safeColStart = Math.floor((dimensions.numCols - safeAreaCols) / 2);
  const safeColEnd = safeColStart + safeAreaCols - 1;

  // Define the ring area (expand safe area by 2 cells in each direction)
  const ringPadding = 2;
  const ringRowStart = Math.max(0, safeRowStart - ringPadding);
  const ringRowEnd = Math.min(dimensions.numRows - 1, safeRowEnd + ringPadding);
  const ringColStart = Math.max(0, safeColStart - ringPadding);
  const ringColEnd = Math.min(dimensions.numCols - 1, safeColEnd + ringPadding);

  // Generate all possible positions, only include those in the inverse ring
  const allPositions = Array.from({ length: totalCells }, (_, idx) => {
    const row = Math.floor(idx / dimensions.numCols);
    const col = idx % dimensions.numCols;
    // Inverse ring: include if inside safe area OR outside the ring area
    const inSafeArea = (
      row >= safeRowStart && row <= safeRowEnd &&
      col >= safeColStart && col <= safeColEnd
    );
    const inRingArea = (
      row >= ringRowStart && row <= ringRowEnd &&
      col >= ringColStart && col <= ringColEnd
    );
    const inRingBand = inRingArea && !inSafeArea;
    if (inSafeArea || !inRingBand) {
      return { row, col };
    }
    return null;
  }).filter((pos): pos is { row: number; col: number } => pos !== null);

  // Shuffle positions
  for (let i = allPositions.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [allPositions[i], allPositions[j]] = [allPositions[j], allPositions[i]];
  }

  // Place colored blocks so that no two are adjacent (including diagonally)
  const coloredSet = new Set<string>();
  const coloredSquares: { row: number; col: number; color: string }[] = [];
  for (const pos of allPositions) {
    const { row, col } = pos;
    // Check all 8 neighbors
    let hasNeighbor = false;
    for (let dr = -1; dr <= 1; dr++) {
      for (let dc = -1; dc <= 1; dc++) {
        if (dr === 0 && dc === 0) continue;
        if (coloredSet.has(`${row + dr},${col + dc}`)) {
          hasNeighbor = true;
          break;
        }
      }
      if (hasNeighbor) break;
    }
    if (!hasNeighbor && coloredSquares.length < maxColored) {
      coloredSet.add(`${row},${col}`);
      coloredSquares.push({
        row,
        col,
        color: tetrisColors[Math.floor(Math.random() * tetrisColors.length)]
      });
    }
    if (coloredSquares.length >= maxColored) break;
  }

  return (
    <div className={cn("min-h-screen relative bg-gradient-to-b from-white via-white to-gray-50/30 overflow-hidden", className)}>
      {/* Grid overlay with larger pattern */}
      <div 
        className="absolute inset-0 z-10 pointer-events-none bg-[linear-gradient(to_right,#e0e0e0_1px,transparent_1px),linear-gradient(to_bottom,#e0e0e0_1px,transparent_1px)] opacity-40"
        style={{
          backgroundSize: `${dimensions.cellSize}px ${dimensions.cellSize}px`
        }}
      />
      
      {/* Colored Tetris squares */}
      {coloredSquares.map((square, i) => (
        <div
          key={i}
          className="absolute z-10 pointer-events-none"
          style={{
            width: `${dimensions.cellSize}px`,
            height: `${dimensions.cellSize}px`,
            top: `${square.row * dimensions.cellSize}px`,
            left: `${square.col * dimensions.cellSize}px`,
            backgroundColor: square.color,
            opacity: 0.15,
            borderRadius: '0.5rem',
          }}
        />
      ))}

      {/* Gradient overlay for depth */}
      <div className="absolute inset-0 z-20 pointer-events-none bg-gradient-to-b from-transparent via-white/5 to-white/20" />
      
      {/* Content container */}
      <div className="relative z-30">
        {children}
      </div>
    </div>
  );
};

export default HeroBackground; 